import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import {
  ArrowRight,
  CalendarDays,
  Check,
  ChevronRight,
  Copy,
  Download,
  FileArchive,
  FileImage,
  FilePlus2,
  FileText,
  Filter,
  ListChecks,
  LocateFixed,
  MapPin,
  Orbit,
  Search,
  Satellite,
  ScanSearch,
  SlidersHorizontal,
  X,
} from "lucide-react"
import { useState } from "react"

import { PhilippinesMap } from "@/components/philippines-map"
import { ScenePreview } from "@/components/scene-preview"
import { SceneStatusBadge } from "@/components/status-badge"
import { WorkspacePageHeader } from "@/components/workspace-page-header"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { SATELLITES } from "@/lib/demo-data"
import type { OrderKind, SceneFilters, SceneType } from "@/lib/domain"
import { formatFileSize, formatResolution } from "@/lib/domain"
import {
  DRAWN_AREA_PREVIEW_ALT,
  DRAWN_AREA_PREVIEW_DISCLAIMER,
  DRAWN_AREA_PREVIEW_PATH,
  formatDrawnAreaLabel,
  serializeDrawnArea,
  serializeDrawnAreaKml,
} from "@/lib/drawn-area"
import type { DrawnAreaFeature } from "@/lib/drawn-area"
import { serviceQueryKeys, scenesQueryOptions } from "@/lib/query-options"
import { cn } from "@/lib/utils"
import { nextGuidedRequestStarted } from "@/lib/guided-request-state"
import { useDemoResetMaintenance } from "@/lib/demo-reset-status"
import { createOrderFn } from "@/server/functions"

const defaultFilters: SceneFilters = {
  query: "",
  type: "Any",
  satellite: "Any satellite",
  maxCloudCover: 25,
  maxResolution: 5,
}

type ExplorerSearch = {
  guided?: boolean
  scene?: string
}

export const Route = createFileRoute("/portal/explorer")({
  validateSearch: (search: Record<string, unknown>): ExplorerSearch => ({
    guided: search.guided === true || search.guided === "true" || undefined,
    scene:
      typeof search.scene === "string" && search.scene.length <= 120
        ? search.scene
        : undefined,
  }),
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(scenesQueryOptions(defaultFilters)),
  head: () => ({
    meta: [{ title: "Imagery explorer · Service Portal demo" }],
  }),
  component: ImageryExplorer,
})

function ImageryExplorer() {
  const resetInProgress = useDemoResetMaintenance()
  const queryClient = useQueryClient()
  const search = Route.useSearch()
  const { data: initialData } = useSuspenseQuery(
    scenesQueryOptions(defaultFilters)
  )
  const [draftFilters, setDraftFilters] = useState<SceneFilters>(defaultFilters)
  const [filters, setFilters] = useState<SceneFilters>(defaultFilters)
  const { data, isFetching } = useQuery(scenesQueryOptions(filters))
  const scenes = data?.scenes ?? initialData.scenes
  const validSearchSceneId =
    search.scene &&
    initialData.scenes.some((scene) => scene.id === search.scene)
      ? search.scene
      : null
  const [selectedSceneId, setSelectedSceneId] = useState<string | null>(
    validSearchSceneId ?? initialData.scenes.at(0)?.id ?? null
  )
  const [selectedIds, setSelectedIds] = useState<Array<string>>(
    validSearchSceneId ? [validSearchSceneId] : []
  )
  const [drawnArea, setDrawnArea] = useState<DrawnAreaFeature | null>(null)
  const [mapViewResetKey, setMapViewResetKey] = useState(0)
  const [geoJsonStatus, setGeoJsonStatus] = useState<
    "idle" | "copied" | "error"
  >("idle")
  const [requestOpen, setRequestOpen] = useState(false)
  const [guidedRequestStarted, setGuidedRequestStarted] = useState(false)
  const [requestKind, setRequestKind] = useState<OrderKind>("Archive")
  const [title, setTitle] = useState("Bicol hazard corridor baseline")
  const [purpose, setPurpose] = useState(
    "Update synthetic hazard exposure layers for a regional planning exercise."
  )
  const [dateRange, setDateRange] = useState("01–27 Jul 2026")
  const [notes, setNotes] = useState(
    "Prioritize low-cloud scenes that include the Mayon–Legazpi corridor."
  )
  const selectedScene =
    scenes.find((scene) => scene.id === selectedSceneId) ??
    initialData.scenes.find((scene) => scene.id === selectedSceneId) ??
    scenes.at(0)
  const areaLabel = drawnArea
    ? formatDrawnAreaLabel(drawnArea)
    : "Legazpi–Daraga corridor · 146.2 km²"
  const drawnAreaGeoJson = drawnArea ? serializeDrawnArea(drawnArea) : null
  const drawnAreaKml = drawnArea ? serializeDrawnAreaKml(drawnArea) : null

  const requestMutation = useMutation({
    mutationFn: () =>
      createOrderFn({
        data: {
          kind: requestKind,
          title,
          purpose,
          areaLabel,
          sceneIds: requestKind === "Archive" ? selectedIds : [],
          requestedSatellite:
            requestKind === "Archive"
              ? (selectedScene?.satellite ?? "Any suitable satellite")
              : draftFilters.satellite === "Any satellite"
                ? "KOMPSAT-5"
                : (draftFilters.satellite ?? "KOMPSAT-5"),
          dateRange,
          notes,
        },
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: serviceQueryKeys.orders() }),
        queryClient.invalidateQueries({
          queryKey: serviceQueryKeys.dashboard(),
        }),
        queryClient.invalidateQueries({ queryKey: serviceQueryKeys.admin() }),
      ])
    },
  })

  function toggleSelected(sceneId: string) {
    setSelectedIds((current) =>
      current.includes(sceneId)
        ? current.filter((id) => id !== sceneId)
        : [...current, sceneId]
    )
  }

  function beginRequest(kind: OrderKind, guided = false) {
    setRequestKind(kind)
    setGuidedRequestStarted(nextGuidedRequestStarted({ type: "open", guided }))
    requestMutation.reset()
    setRequestOpen(true)
  }

  function closeRequest() {
    setRequestOpen(false)
    setGuidedRequestStarted(nextGuidedRequestStarted({ type: "close" }))
    requestMutation.reset()
  }

  async function copyDrawnAreaGeoJson() {
    if (!drawnAreaGeoJson) return

    try {
      await navigator.clipboard.writeText(drawnAreaGeoJson)
      setGeoJsonStatus("copied")
    } catch {
      setGeoJsonStatus("error")
    }
  }

  function downloadDrawnAreaKml() {
    if (!drawnAreaKml) return

    const file = new Blob([drawnAreaKml], {
      type: "application/vnd.google-earth.kml+xml;charset=utf-8",
    })
    const url = URL.createObjectURL(file)
    const link = document.createElement("a")
    link.href = url
    link.download = "custom-area-of-interest.kml"
    link.click()
    window.setTimeout(() => URL.revokeObjectURL(url), 0)
  }

  return (
    <Dialog
      open={requestOpen}
      onOpenChange={(open) => {
        if (open) setRequestOpen(true)
        else closeRequest()
      }}
    >
      <div
        className={cn(
          "portal-explorer mx-auto max-w-[1700px]",
          drawnArea && "has-drawn-area"
        )}
      >
        <WorkspacePageHeader
          kicker="Archive and acquisition planning"
          title="Imagery explorer"
          description="Define an area, apply sensor-aware filters, inspect synthetic scene metadata, and move the right coverage into a request."
          actions={
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => beginRequest("New tasking")}
                disabled={resetInProgress}
              >
                <Orbit />
                New tasking
              </Button>
              <Button
                type="button"
                onClick={() => beginRequest("Archive")}
                disabled={resetInProgress || selectedIds.length === 0}
              >
                <FilePlus2 />
                Request selected
                {selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}
              </Button>
            </>
          }
        />

        {search.guided ? (
          <section
            aria-labelledby="guided-demo-title"
            className="mt-5 grid gap-4 rounded-[14px] border border-[#9ccbd4] bg-[#eaf7f8] p-4 sm:grid-cols-[auto_1fr_auto] sm:items-center sm:p-5"
          >
            <span className="grid size-11 place-items-center rounded-[10px] bg-[var(--navy)] text-[var(--cyan)]">
              <ListChecks className="size-5" />
            </span>
            <div>
              <p className="text-[0.68rem] font-semibold tracking-[0.06em] text-[var(--service-blue)] uppercase">
                Guided demo · step 1 of 2 · about 3 minutes
              </p>
              <h2
                id="guided-demo-title"
                className="mt-1 font-semibold text-[var(--navy)]"
              >
                Review the preselected Legazpi scene
              </h2>
              <p className="mt-1 text-xs leading-5 text-[#315b68]">
                The area, low-cloud optical scene, intended use, and technical
                notes are ready. Submit the request to reach a real completion
                state, or dismiss the guide and explore every filter yourself.
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:items-stretch">
              <Button
                type="button"
                className="shrink-0"
                onClick={() => {
                  beginRequest("Archive", true)
                }}
                disabled={resetInProgress}
              >
                Create guided request
                <ArrowRight />
              </Button>
              <Button
                render={
                  <Link
                    to="/portal/explorer"
                    search={{ scene: search.scene }}
                  />
                }
                variant="outline"
                className="shrink-0 border-[#9ccbd4] bg-white"
              >
                Explore freely
              </Button>
            </div>
          </section>
        ) : null}

        <div className="explorer-stage relative mt-5 min-h-[760px] overflow-hidden rounded-[14px] border border-border bg-[var(--console-void)]">
          <aside className="explorer-drawer min-w-0 border-b border-border bg-[var(--console-panel)] lg:absolute lg:top-3 lg:left-3 lg:z-30 lg:flex lg:h-[820px] lg:w-[360px] lg:flex-col lg:overflow-hidden lg:rounded-[14px] lg:border lg:shadow-[0_20px_52px_-28px_rgba(18,58,90,.48)]">
            <div className="border-b border-border p-4">
              <div className="flex items-center justify-between gap-3">
                <p className="flex items-center gap-2 text-xs font-semibold text-[var(--navy)]">
                  <MapPin className="size-4 text-[var(--service-blue)]" />
                  Area of interest
                </p>
                <span className="text-xs text-muted-foreground">Demo area</span>
              </div>
              <div className="relative mt-3">
                <Input
                  value="Legazpi City, Albay"
                  readOnly
                  aria-label="Area search"
                  className="h-11 rounded-[10px] border-border bg-white pr-10"
                />
                <Search className="pointer-events-none absolute top-3.5 right-3 size-4 text-muted-foreground" />
              </div>
              <div className="mt-3 flex items-center justify-between gap-3 rounded-[10px] bg-[#eaf7f8] px-3 py-2.5">
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-[#164b59]">
                    {drawnArea
                      ? "Custom drawn area"
                      : "Legazpi–Daraga corridor"}
                  </p>
                  <p className="mt-0.5 text-[0.65rem] text-[#42717b]">
                    {drawnArea
                      ? `Polygon · ${drawnArea.properties.areaSquareKilometers.toLocaleString(
                          "en-US",
                          { maximumFractionDigits: 2 }
                        )} km²`
                      : "Rectangle · 146.2 km²"}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Center map on area"
                  title="Center map on area"
                  className="instrument-control explorer-locate text-[var(--service-blue)]"
                  onClick={() => setMapViewResetKey((value) => value + 1)}
                >
                  <LocateFixed />
                </Button>
              </div>
              <p className="mt-2 text-[0.65rem] leading-4 text-[#526872]">
                Boundary exchange: zipped Shapefile or KML/KMZ.
              </p>
            </div>

            <div className="border-b border-border p-4">
              <div className="flex items-center justify-between gap-3">
                <p className="flex items-center gap-2 text-xs font-semibold text-[var(--navy)]">
                  <SlidersHorizontal className="size-4 text-[var(--service-blue)]" />
                  Catalogue filters
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  onClick={() => {
                    setDraftFilters(defaultFilters)
                    setFilters(defaultFilters)
                  }}
                >
                  Clear
                </Button>
              </div>

              <div className="mt-4 grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="scene-keyword">Keyword</Label>
                  <Input
                    id="scene-keyword"
                    value={draftFilters.query ?? ""}
                    onChange={(event) =>
                      setDraftFilters((current) => ({
                        ...current,
                        query: event.target.value,
                      }))
                    }
                    placeholder="Scene ID, place, mission"
                    className="h-11 rounded-[10px] border-border bg-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="grid min-w-0 gap-2">
                    <Label>Image type</Label>
                    <Select
                      value={draftFilters.type ?? "Any"}
                      onValueChange={(value) =>
                        setDraftFilters((current) => ({
                          ...current,
                          type: value as "Any" | SceneType,
                        }))
                      }
                    >
                      <SelectTrigger
                        aria-label="Filter imagery by type"
                        className="h-11 w-full rounded-[10px] border-border bg-white"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Any">Any type</SelectItem>
                        <SelectItem value="OPTICAL">Optical</SelectItem>
                        <SelectItem value="SAR">SAR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid min-w-0 gap-2">
                    <Label>Satellite</Label>
                    <Select
                      value={draftFilters.satellite ?? "Any satellite"}
                      onValueChange={(value) =>
                        setDraftFilters((current) => ({
                          ...current,
                          satellite: value as string,
                        }))
                      }
                    >
                      <SelectTrigger
                        aria-label="Filter imagery by satellite"
                        className="h-11 w-full rounded-[10px] border-border bg-white"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SATELLITES.map((satellite) => (
                          <SelectItem key={satellite} value={satellite}>
                            {satellite}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid gap-2">
                  <div className="flex items-center justify-between gap-3">
                    <Label htmlFor="cloud-cover">Maximum cloud cover</Label>
                    <span className="text-xs font-semibold text-[var(--service-blue)]">
                      {draftFilters.maxCloudCover ?? 25}%
                    </span>
                  </div>
                  <input
                    id="cloud-cover"
                    type="range"
                    min="0"
                    max="60"
                    step="5"
                    value={draftFilters.maxCloudCover ?? 25}
                    onChange={(event) =>
                      setDraftFilters((current) => ({
                        ...current,
                        maxCloudCover: Number(event.target.value),
                      }))
                    }
                    className="service-range w-full"
                  />
                </div>
                <div className="grid gap-2">
                  <div className="flex items-center justify-between gap-3">
                    <Label htmlFor="resolution">
                      Maximum spatial resolution
                    </Label>
                    <span className="text-xs font-semibold text-[var(--service-blue)]">
                      ≤ {draftFilters.maxResolution ?? 5} m
                    </span>
                  </div>
                  <input
                    id="resolution"
                    type="range"
                    min="0.5"
                    max="20"
                    step="0.5"
                    value={draftFilters.maxResolution ?? 5}
                    onChange={(event) =>
                      setDraftFilters((current) => ({
                        ...current,
                        maxResolution: Number(event.target.value),
                      }))
                    }
                    className="service-range w-full"
                  />
                </div>
                <Button
                  type="button"
                  onClick={() => setFilters(draftFilters)}
                  disabled={isFetching}
                  className="w-full"
                >
                  <Filter />
                  {isFetching ? "Updating results…" : "Apply filters"}
                </Button>
              </div>
            </div>

            <div className="max-h-[520px] overflow-y-auto lg:max-h-none lg:min-h-0 lg:flex-1">
              <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-border bg-white px-4 py-3">
                <p className="text-xs font-semibold text-[var(--navy)]">
                  Matching scenes
                </p>
                <span className="text-[0.65rem] text-muted-foreground">
                  {scenes.length} shown · synthetic
                </span>
              </div>
              {scenes.length > 0 ? (
                <div className="divide-y divide-border">
                  {scenes.map((scene) => {
                    const isSelected = selectedIds.includes(scene.id)
                    const isActive = scene.id === selectedSceneId
                    return (
                      <article
                        key={scene.id}
                        className={cn(
                          "group relative p-4 transition-colors",
                          isActive ? "bg-[#eef7f8]" : "hover:bg-[var(--cloud)]"
                        )}
                      >
                        <div className="flex gap-3">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleSelected(scene.id)}
                            aria-label={`Select ${scene.id} for request`}
                            className="mt-1"
                          />
                          <button
                            type="button"
                            onClick={() => setSelectedSceneId(scene.id)}
                            className="min-w-0 flex-1 text-left focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-[var(--cyan)]"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="truncate text-xs font-semibold text-[var(--service-blue)]">
                                  {scene.satellite} · {scene.type}
                                </p>
                                <h3 className="mt-1 truncate text-sm font-semibold text-[var(--navy)]">
                                  {scene.location}
                                </h3>
                              </div>
                              <ChevronRight
                                className={cn(
                                  "mt-1 size-4 shrink-0 text-muted-foreground transition-transform",
                                  isActive &&
                                    "translate-x-1 text-[var(--service-blue)]"
                                )}
                              />
                            </div>
                            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[0.65rem] text-muted-foreground">
                              <span>{scene.capturedLabel.split(" · ")[0]}</span>
                              <span>
                                {formatResolution(scene.resolutionMeters)}
                              </span>
                              <span>
                                {scene.cloudCover === null
                                  ? scene.polarization
                                  : `${scene.cloudCover}% cloud`}
                              </span>
                            </div>
                          </button>
                        </div>
                      </article>
                    )
                  })}
                </div>
              ) : (
                <div className="px-5 py-12 text-center">
                  <ScanSearch className="mx-auto size-7 text-[var(--telemetry)]" />
                  <h3 className="mt-3 text-sm font-semibold text-[var(--navy)]">
                    No scenes match these filters
                  </h3>
                  <p className="mt-2 text-xs leading-5 text-muted-foreground">
                    Increase cloud cover or resolution, or return to any
                    satellite.
                  </p>
                </div>
              )}
            </div>
          </aside>

          <div className="explorer-canvas min-w-0 bg-[var(--console-void)] p-3">
            <PhilippinesMap
              scenes={scenes}
              selectedSceneId={selectedSceneId}
              onSelectScene={setSelectedSceneId}
              drawingEnabled
              drawnArea={drawnArea}
              onDrawnAreaChange={(feature) => {
                setDrawnArea(feature)
                setGeoJsonStatus("idle")
              }}
              areaLabel={areaLabel}
              viewResetKey={mapViewResetKey}
              className="min-h-[520px] lg:min-h-[820px]"
            />

            {drawnArea && drawnAreaGeoJson ? (
              <section
                className="mt-3 overflow-hidden rounded-[14px] border border-[#b9cdd2] bg-white"
                aria-labelledby="drawn-area-title"
              >
                <div className="grid md:grid-cols-[260px_minmax(0,1fr)]">
                  <div className="relative min-h-[210px] overflow-hidden bg-[#173f58]">
                    <img
                      src={DRAWN_AREA_PREVIEW_PATH}
                      alt={DRAWN_AREA_PREVIEW_ALT}
                      className="absolute inset-0 size-full object-cover"
                    />
                    <span className="absolute top-3 left-3 rounded-[6px] bg-[#102f43]/94 px-2 py-1 text-[0.65rem] font-semibold text-white shadow-[0_6px_18px_-10px_rgba(16,47,67,.8)]">
                      Synthetic preview · JPG
                    </span>
                  </div>
                  <div className="min-w-0 p-4 sm:p-5">
                    <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-start">
                      <div className="min-w-0">
                        <p className="flex items-center gap-2 text-xs font-semibold text-[var(--service-blue)]">
                          <MapPin className="size-4" />
                          AOI geometry ready
                        </p>
                        <h2
                          id="drawn-area-title"
                          className="mt-1 text-lg font-semibold tracking-[-0.02em] text-[var(--navy)]"
                        >
                          Custom area ready for imagery search
                        </h2>
                        <p className="mt-2 max-w-[74ch] text-xs leading-5 text-[#526872]">
                          The browser keeps this boundary as GeoJSON for map
                          operations. Boundary exchange uses a zipped Shapefile
                          or KML/KMZ; satellite product files are delivered
                          separately.
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-wrap gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={copyDrawnAreaGeoJson}
                        >
                          <Copy />
                          {geoJsonStatus === "copied"
                            ? "Copied"
                            : "Copy internal GeoJSON"}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          onClick={downloadDrawnAreaKml}
                        >
                          <Download />
                          Download KML
                        </Button>
                      </div>
                    </div>
                    <dl className="mt-4 grid border-y border-border sm:grid-cols-2">
                      <div className="py-3 sm:pr-4">
                        <dt className="flex items-center gap-2 text-[0.65rem] font-semibold text-[var(--navy)]">
                          <FileArchive className="size-3.5 text-[var(--service-blue)]" />
                          AOI boundary exchange
                        </dt>
                        <dd className="mt-1 text-xs text-[#526872]">
                          Shapefile <code>.zip</code> · KML/KMZ
                        </dd>
                      </div>
                      <div className="border-t border-border py-3 sm:border-t-0 sm:border-l sm:pl-4">
                        <dt className="flex items-center gap-2 text-[0.65rem] font-semibold text-[var(--navy)]">
                          <FileImage className="size-3.5 text-[var(--service-blue)]" />
                          Satellite product package
                        </dt>
                        <dd className="mt-1 text-xs text-[#526872]">
                          GeoTIFF <code>.tif</code> · metadata <code>.xml</code>
                        </dd>
                      </div>
                    </dl>
                    <p
                      className={cn(
                        "mt-3 text-xs font-medium",
                        geoJsonStatus === "error"
                          ? "text-[#9a3040]"
                          : "text-[#246947]"
                      )}
                      aria-live="polite"
                    >
                      {geoJsonStatus === "copied"
                        ? "GeoJSON copied to the clipboard."
                        : geoJsonStatus === "error"
                          ? "Clipboard access failed. Use the KML download instead."
                          : DRAWN_AREA_PREVIEW_DISCLAIMER}
                    </p>
                  </div>
                </div>
                <details className="group border-t border-border">
                  <summary className="flex min-h-11 cursor-pointer items-center gap-2 px-4 text-xs font-semibold text-[var(--navy)] hover:bg-[var(--cloud)] focus-visible:outline-3 focus-visible:outline-offset-[-3px] focus-visible:outline-[var(--service-blue)] sm:px-5">
                    <ChevronRight className="size-4 transition-transform group-open:rotate-90" />
                    Inspect internal GeoJSON
                  </summary>
                  <pre className="max-h-72 overflow-auto border-t border-border bg-[#102f43] p-4 font-mono text-[0.72rem] leading-5 text-[#eef8fa] sm:p-5">
                    <code>{drawnAreaGeoJson}</code>
                  </pre>
                </details>
              </section>
            ) : null}

            {selectedScene ? (
              <section
                className={cn(
                  "scene-tray mt-3 grid overflow-hidden rounded-[14px] border border-border bg-white lg:grid-cols-[190px_minmax(0,1fr)]",
                  !drawnArea &&
                    "lg:relative lg:z-30 lg:-mt-[238px] lg:mr-3 lg:ml-[380px] lg:shadow-[0_20px_52px_-28px_rgba(18,58,90,.48)]"
                )}
              >
                <ScenePreview
                  scene={selectedScene}
                  className="min-h-[190px] rounded-none lg:min-h-full"
                />
                <div className="min-w-0 p-4">
                  <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold text-[var(--service-blue)]">
                          {selectedScene.satellite} · {selectedScene.type}
                        </span>
                        <SceneStatusBadge status={selectedScene.status} />
                      </div>
                      <h2 className="mt-2 text-xl font-semibold tracking-[-0.02em] text-[var(--navy)]">
                        {selectedScene.location}
                      </h2>
                      <p className="mt-1 font-mono text-[0.65rem] text-muted-foreground">
                        {selectedScene.id}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant={
                        selectedIds.includes(selectedScene.id)
                          ? "secondary"
                          : "default"
                      }
                      onClick={() => toggleSelected(selectedScene.id)}
                    >
                      {selectedIds.includes(selectedScene.id) ? (
                        <>
                          <X />
                          Remove selection
                        </>
                      ) : (
                        <>
                          <Check />
                          Add to request
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="mt-3 max-w-[72ch] text-xs leading-5 text-muted-foreground">
                    {selectedScene.description}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 border-y border-border py-2.5 text-[0.68rem] text-[#526872]">
                    <span className="inline-flex items-center gap-1.5">
                      <FileImage className="size-3.5 text-[var(--service-blue)]" />
                      GeoTIFF imagery <code>.tif</code>
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <FileText className="size-3.5 text-[var(--service-blue)]" />
                      XML metadata <code>.xml</code>
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <FileImage className="size-3.5 text-[var(--service-blue)]" />
                      Preview <code>.jpg/.png</code>
                    </span>
                  </div>
                  <dl className="mt-3 grid grid-cols-2 gap-x-5 gap-y-3 sm:grid-cols-3 xl:grid-cols-6">
                    {[
                      ["Captured", selectedScene.capturedLabel],
                      [
                        "Resolution",
                        formatResolution(selectedScene.resolutionMeters),
                      ],
                      [
                        selectedScene.type === "SAR" ? "Polarization" : "Cloud",
                        selectedScene.type === "SAR"
                          ? (selectedScene.polarization ?? "N/A")
                          : `${selectedScene.cloudCover}%`,
                      ],
                      ["Bands", selectedScene.band],
                      ["Product", selectedScene.productLevel],
                      ["Demo size", formatFileSize(selectedScene.fileSizeGb)],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <dt className="text-xs font-medium text-muted-foreground">
                          {label}
                        </dt>
                        <dd className="mt-1 text-xs font-semibold text-[var(--ink)]">
                          {value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </section>
            ) : null}
          </div>
        </div>

        <DialogContent className="portal-console max-h-[92svh] overflow-y-auto rounded-[14px] sm:max-w-[720px]">
          {requestMutation.data ? (
            <div className="py-2">
              <span className="grid size-12 place-items-center rounded-[12px] bg-[#eaf6ef] text-[#246947]">
                <Check className="size-5" />
              </span>
              <DialogHeader className="mt-5">
                <DialogTitle className="text-2xl font-semibold tracking-[-0.025em] text-[var(--navy)]">
                  {search.guided && guidedRequestStarted
                    ? "Guided scenario complete"
                    : "Demo request submitted"}
                </DialogTitle>
                <DialogDescription className="leading-6">
                  The synthetic request is now visible in both the analyst order
                  history and administrator queue. No production service was
                  contacted.
                </DialogDescription>
              </DialogHeader>
              <div className="mt-5 rounded-[12px] border border-border bg-[var(--cloud)] p-4">
                <p className="font-mono text-xs font-semibold text-[var(--service-blue)]">
                  {requestMutation.data.id}
                </p>
                <p className="mt-2 font-semibold text-[var(--navy)]">
                  {requestMutation.data.title}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {requestMutation.data.kind} · Submitted · Just now
                </p>
              </div>
              <DialogFooter className="mt-6">
                <DialogClose
                  render={
                    <Button
                      type="button"
                      variant="outline"
                      onClick={closeRequest}
                    />
                  }
                >
                  Close
                </DialogClose>
                <Button render={<Link to="/portal/orders" />}>
                  Open order history
                  <ArrowRight />
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <form
              onSubmit={(event) => {
                event.preventDefault()
                requestMutation.mutate()
              }}
            >
              <DialogHeader>
                <p className="text-xs font-semibold text-[var(--service-blue)]">
                  {requestKind === "Archive"
                    ? `${selectedIds.length} archive scene${
                        selectedIds.length === 1 ? "" : "s"
                      } selected`
                    : "New satellite acquisition"}
                </p>
                <DialogTitle className="mt-1 text-2xl font-semibold tracking-[-0.025em] text-[var(--navy)]">
                  Create {requestKind.toLowerCase()} request
                </DialogTitle>
                <DialogDescription className="leading-6">
                  This submits a synthetic order to the demo operations queue.
                  No production service is contacted.
                </DialogDescription>
              </DialogHeader>

              <div className="mt-6 grid gap-5">
                <div className="grid gap-2">
                  <Label htmlFor="request-title">Request title</Label>
                  <Input
                    id="request-title"
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    minLength={4}
                    maxLength={120}
                    required
                    className="h-11 rounded-[10px] border-border bg-white"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="request-purpose">Intended use</Label>
                  <Textarea
                    id="request-purpose"
                    value={purpose}
                    onChange={(event) => setPurpose(event.target.value)}
                    minLength={12}
                    maxLength={800}
                    required
                    rows={4}
                    className="rounded-[10px] border-border bg-white"
                  />
                  <p className="text-[0.68rem] leading-5 text-muted-foreground">
                    Explain the government program, operational need, or
                    research question this imagery supports.
                  </p>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="grid gap-2">
                    <Label htmlFor="request-area">Area of interest</Label>
                    <div className="relative">
                      <Input
                        id="request-area"
                        readOnly
                        value={areaLabel}
                        className="h-11 rounded-[10px] border-border bg-[var(--cloud)] pl-10"
                      />
                      <MapPin className="absolute top-3.5 left-3 size-4 text-[var(--service-blue)]" />
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="request-dates">Date range</Label>
                    <div className="relative">
                      <Input
                        id="request-dates"
                        value={dateRange}
                        onChange={(event) => setDateRange(event.target.value)}
                        required
                        className="h-11 rounded-[10px] border-border bg-white pl-10"
                      />
                      <CalendarDays className="absolute top-3.5 left-3 size-4 text-[var(--service-blue)]" />
                    </div>
                  </div>
                </div>

                {requestKind === "Archive" ? (
                  <div className="rounded-[12px] border border-border">
                    <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
                      <p className="text-xs font-semibold text-[var(--navy)]">
                        Selected scenes
                      </p>
                      <span className="text-[0.65rem] text-muted-foreground">
                        {selectedIds.length} scene
                        {selectedIds.length === 1 ? "" : "s"}
                      </span>
                    </div>
                    <div className="max-h-36 divide-y divide-border overflow-y-auto">
                      {selectedIds.map((id) => {
                        const scene = initialData.scenes.find(
                          (item) => item.id === id
                        )
                        if (!scene) return null
                        return (
                          <div
                            key={scene.id}
                            className="flex items-center justify-between gap-3 px-4 py-3"
                          >
                            <div className="min-w-0">
                              <p className="truncate text-xs font-semibold text-[var(--ink)]">
                                {scene.satellite} · {scene.location}
                              </p>
                              <p className="mt-0.5 truncate font-mono text-xs text-muted-foreground">
                                {scene.id}
                              </p>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon-xs"
                              onClick={() => toggleSelected(scene.id)}
                              aria-label={`Remove ${scene.id}`}
                            >
                              <X />
                            </Button>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="rounded-[12px] border border-[#b8dbe1] bg-[#eaf7f8] p-4">
                    <p className="flex items-center gap-2 text-xs font-semibold text-[#164b59]">
                      <Satellite className="size-4" />
                      Requested mission ·{" "}
                      {draftFilters.satellite === "Any satellite"
                        ? "KOMPSAT-5"
                        : draftFilters.satellite}
                    </p>
                    <p className="mt-2 text-[0.68rem] leading-5 text-[#42717b]">
                      Demo operations will review orbit, sensor fit, weather,
                      and acquisition-window constraints.
                    </p>
                  </div>
                )}

                <div className="grid gap-2">
                  <Label htmlFor="request-notes">Technical notes</Label>
                  <Textarea
                    id="request-notes"
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    maxLength={800}
                    rows={3}
                    className="rounded-[10px] border-border bg-white"
                  />
                </div>
              </div>

              {requestMutation.error ? (
                <p
                  className="mt-4 rounded-[10px] bg-[#fff0f2] px-3 py-2.5 text-sm text-[#8f2d3a]"
                  role="alert"
                >
                  {requestMutation.error.message}
                </p>
              ) : null}

              <DialogFooter className="mt-6 border-t border-border pt-5">
                <DialogClose
                  render={
                    <Button
                      type="button"
                      variant="outline"
                      onClick={closeRequest}
                    />
                  }
                >
                  Cancel
                </DialogClose>
                <Button
                  type="submit"
                  disabled={
                    resetInProgress ||
                    requestMutation.isPending ||
                    (requestKind === "Archive" && selectedIds.length === 0)
                  }
                >
                  {requestMutation.isPending
                    ? "Submitting…"
                    : "Submit demo request"}
                  <ArrowRight />
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </div>
    </Dialog>
  )
}
